import { Colorpicker, ColorpickerEvents, isHex } from "@dhx/ts-colorpicker";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { create, el } from "@dhx/ts-common/dom";
import { Label } from "./helper/label";
import { Popup } from "@dhx/ts-popup";
import { ValidationStatus, IColorpickerInput, BaseElementEvent, IBaseColorpickerEventHandlersMap } from "../types";
import { getFormItemCss, getValidationMessage, isEmptyObj } from "../helper";

export class ColorpickerInput extends Label {
	public config: IColorpickerInput;
	public colorpicker: Colorpicker;
	public events: IEventSystem<BaseElementEvent, IBaseColorpickerEventHandlersMap> = new EventSystem();
	private _popup: Popup;

	constructor(container, config: IColorpickerInput) {
		super(null, config);

		this.setConfig(config);
		const render = () => this._draw();
		this.mount(container, create({ render }));
	}
	validate(): boolean {
		const { required, value, validation } = this.config;
		let isValid = true;
		isValid = validation
			? this.config.validation(value)
			: !required || Boolean(value);
		this.config.$validationStatus = isValid
			? ValidationStatus.success
			: ValidationStatus.error;
		this.paint();
		return isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	setValue(value: string): void {
		this.config.value = value;
		this.colorpicker.setValue(value);
		this.paint();
	}
	setConfig(config: IColorpickerInput): void {
		if (isEmptyObj(config)) {
			return;
		}
		if (this.colorpicker) {
			this.colorpicker.destructor();
		}
		if (this._popup) {
			this._popup.destructor();
		}
		this.config = {
			type: this.config.type,
			id: this.config.id,
			name: this.config.name,
			value: ""
		};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}
		this._popup = new Popup({css: "dhx_widget--border-shadow"});
		this.colorpicker = new Colorpicker(null, config);
		this._popup.attach(this.colorpicker);

		if (this.config.value) {
			this.colorpicker.setValue(this.config.value);
			this.config.value = this.colorpicker.getValue();
		}
		this.colorpicker.events.on(ColorpickerEvents.change, () => {
			this.config.value = this.colorpicker.getValue() as string;
			this._popup.hide();
			this.validate();
		});
		this.events.on(BaseElementEvent.change, value => {
			const validValue = this._inputValidate(value);
			if (this._inputValidate(value)) {
				this.setValue(validValue);
			} else {
				this.validate();
			}
		});

		this.events.fire(BaseElementEvent.configUpdate, [this.config]);
		this.clearValidate();
	}
	getValue(): string {
		return this.config.value || "";
	}
	clear(): void {
		this.config.value = "";
		this.paint();
	}
	getWidget(): Colorpicker {
		return this.colorpicker;
	}
	protected _getHandlers() {
		return {
			onfocus: () => {
				if (this._popup.isVisible()) {
					return;
				}
				const node = this.getRootView().refs.input.el;
				this._popup.show(node);
			},
			onchange: (e: Event) => {
				const value = (e.target as HTMLInputElement).value;
				this.events.fire(BaseElementEvent.change, [value]);
			},
			onkeyup: (e: KeyboardEvent) => {
				if (e.keyCode === 13) {
					if (this._popup.isVisible()) {
						this._popup.hide();
					}
					const node = this.getRootView().refs.input.el;
					node.blur();
				}
			}
		};
	}
	protected _inputValidate(value: string): string {
		return isHex(value) ? value : "";
	}
	protected _draw() {
		const { required, value, icon, disabled, placeholder, name, id, editing = false } = this.config;
		return el("div.dhx_form-group", {
			class: getFormItemCss(this.config, Boolean(required) || Boolean(this.config.validation)),
		}, [
			this._drawLabel(),
			el(".dhx_input__wrapper", [
				el("div.dhx_input__container", {
				}, [
					el(".dhx_input__icon", {
						class: icon || "dxi dxi-eyedropper-variant" +
						(value ? " dhx_input__icon--color-selected" :  ""),
						style: {"background-color": value || "transparent" }
					}),
					el("input.dhx_input.dhx_input--icon-padding", {
						_key: this._uid,
						value,
						type: "text",
						_ref: "input",
						required,
						disabled,
						placeholder: placeholder || "",
						name: name || "",
						id: id || this._uid,
						onfocus: this._handlers.onfocus,
						onchange: this._handlers.onchange,
						onkeyup: this._handlers.onkeyup,
						autocomplete: "off",
						readOnly: !editing
					}),
				]),
				getValidationMessage(this.config) && el("span.dhx_input__caption", {
				}, getValidationMessage(this.config))
			]),
		]);
	}
}
